import React, { useState, useEffect, useCallback } from 'react';
import { Megaphone, Lock, Save, Eye, EyeOff, BarChart3, FileText, LogOut, KeyRound, Upload, CheckCircle2, ShieldCheck, Send, FileSearch, ExternalLink } from 'lucide-react';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import AdminContenuti from '../components/AdminContenuti';
import AdminModuli from '../components/AdminModuli';
import AdminRichiesteModuli from '../components/AdminRichiesteModuli';

const API = process.env.REACT_APP_BACKEND_URL;
const fj = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });

const DEFAULT_IMPACT = {
  title: 'Il Nostro Impegno per il Terzo Settore',
  subtitle: 'Numeri che raccontano il nostro impatto quotidiano nel supportare chi opera per il bene comune.',
  date_note: 'Dal 21.02.2024 ad oggi',
  counters: [
    { id: 'rca', value: 409, label: "Richieste RCA 'Basta la Targa' gestite con successo" },
    { id: 'quotazioni', value: 283, label: 'Schede di Quotazione e Analisi Rischi per Cooperative e Enti No-Profit' },
    { id: 'consulenze', value: 44, label: 'Consulenze specialistiche prenotate online' },
    { id: 'ore_gratuite', value: 160, label: 'Ore di Consulenza Gratuita donate agli Enti del Terzo Settore' },
  ],
};

const COUNTER_NAMES = {
  rca: 'Contatore 1 — RCA',
  quotazioni: 'Contatore 2 — Quotazioni',
  consulenze: 'Contatore 3 — Consulenze',
  ore_gratuite: 'Contatore 4 — Ore gratuite (Società Benefit)',
};

const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2380]";

export default function AdminAvvisi() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [text, setText] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [active, setActive] = useState(true);
  const [impact, setImpact] = useState(DEFAULT_IMPACT);
  const [savingImpact, setSavingImpact] = useState(false);
  const [docs, setDocs] = useState([]);
  const [riskReqs, setRiskReqs] = useState([]);
  const [riskNote, setRiskNote] = useState({});
  const [validating, setValidating] = useState(null);
  const [partnerCode, setPartnerCode] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [pwdForm, setPwdForm] = useState({ current: '', nuova: '' });
  const [changingPwd, setChangingPwd] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [annRes, impRes, docsRes, riskRes] = await Promise.all([
        fj('/api/admin/announcement'),
        fj('/api/impact'),
        fj('/api/admin/docs'),
        fj('/api/admin/risk-requests'),
      ]);
      const ann = await annRes.json();
      if (ann.announcement) {
        setText(ann.announcement.text || '');
        setExpiresAt((ann.announcement.expires_at || '').slice(0, 10));
        setActive(ann.announcement.active !== false);
      }
      const imp = await impRes.json();
      if (imp.impact) {
        setImpact({
          title: imp.impact.title || DEFAULT_IMPACT.title,
          subtitle: imp.impact.subtitle || DEFAULT_IMPACT.subtitle,
          date_note: imp.impact.date_note || DEFAULT_IMPACT.date_note,
          counters: DEFAULT_IMPACT.counters.map((b) => {
            const s = (imp.impact.counters || []).find((c) => c.id === b.id);
            return s ? { ...b, value: s.value, label: s.label } : b;
          }),
        });
      }
      const d = await docsRes.json();
      setDocs(d.docs || []);
      const rk = await riskRes.json();
      setRiskReqs(rk.richieste || []);
    } catch {}
  }, []);

  useEffect(() => {
    fj('/api/auth/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => { if (u) { setUser(u); loadAll(); } })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fj('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Credenziali non valide');
        return;
      }
      setUser(data);
      setPassword('');
      loadAll();
      toast.success(`Benvenuto, ${data.name}!`);
    } catch {
      toast.error('Errore di connessione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await fj('/api/auth/logout', { method: 'POST' });
    setUser(null);
  };

  const forgot = async () => {
    if (!email) { toast.error('Inserisci prima la tua email nel campo sopra'); return; }
    await fj('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    toast.success('Se l\'email è registrata, riceverai il link per reimpostare la password.');
  };

  const saveAnnouncement = async (e) => {
    e.preventDefault();
    if (!text.trim()) { toast.error('Inserisci il testo dell\'avviso'); return; }
    if (!expiresAt) { toast.error('Imposta la data di scadenza'); return; }
    setLoading(true);
    try {
      const res = await fj('/api/admin/announcement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim(), expires_at: expiresAt, active }),
      });
      res.ok ? toast.success('Avviso salvato con successo') : toast.error('Errore durante il salvataggio');
    } catch { toast.error('Errore di connessione'); } finally { setLoading(false); }
  };

  const updateCounter = (id, field, value) => {
    setImpact((p) => ({ ...p, counters: p.counters.map((c) => (c.id === id ? { ...c, [field]: value } : c)) }));
  };

  const saveImpact = async (e) => {
    e.preventDefault();
    setSavingImpact(true);
    try {
      const res = await fj('/api/admin/impact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: impact.title.trim(), subtitle: impact.subtitle.trim(), date_note: impact.date_note.trim(),
          counters: impact.counters.map((c) => ({ ...c, value: parseInt(c.value, 10) || 0 })),
        }),
      });
      res.ok ? toast.success('Statistiche salvate con successo') : toast.error('Errore durante il salvataggio');
    } catch { toast.error('Errore di connessione'); } finally { setSavingImpact(false); }
  };

  const uploadDoc = async (docId, file) => {
    if (!file) return;
    setUploadingDoc(docId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fj(`/api/admin/docs/${docId}`, { method: 'POST', body: fd });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); loadAll(); }
      else toast.error(typeof data.detail === 'string' ? data.detail : 'Errore nel caricamento');
    } catch { toast.error('Errore di connessione'); } finally { setUploadingDoc(null); }
  };

  const verificaCfPiva = (cf) => {
    const isPiva = /^\d{11}$/.test(cf || '');
    try { navigator.clipboard.writeText(cf); } catch { /* clipboard non disponibile */ }
    window.open(
      isPiva
        ? 'https://ec.europa.eu/taxation_customs/vies/#/vat-validation'
        : 'https://telematici.agenziaentrate.gov.it/VerificaCF/Scegli.do?parameter=verificaCf',
      '_blank', 'noopener'
    );
    toast.info(`${cf} copiato negli appunti: incollalo nel campo di verifica ${isPiva ? 'VIES' : 'Agenzia delle Entrate'}`);
  };

  const previewRiskPdf = async (rid) => {
    try {
      const res = await fj(`/api/admin/risk-requests/${rid}/pdf`);
      if (!res.ok) { toast.error('Anteprima non disponibile'); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Errore di connessione'); }
  };

  const validaRisk = async (rid) => {
    setValidating(rid);
    try {
      const res = await fj(`/api/admin/risk-requests/${rid}/valida`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nota: riskNote[rid] || '' }),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); loadAll(); }
      else toast.error(typeof data.detail === 'string' ? data.detail : 'Errore');
    } catch { toast.error('Errore di connessione'); } finally { setValidating(null); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwdForm.nuova.length < 8) { toast.error('La nuova password deve avere almeno 8 caratteri'); return; }
    setChangingPwd(true);
    try {
      const res = await fj('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: pwdForm.current, new_password: pwdForm.nuova }),
      });
      const data = await res.json();
      if (res.ok) { toast.success('Password aggiornata'); setPwdForm({ current: '', nuova: '' }); }
      else toast.error(typeof data.detail === 'string' ? data.detail : 'Errore');
    } catch { toast.error('Errore di connessione'); } finally { setChangingPwd(false); }
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-gray-400">Caricamento...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B2380] to-[#0E8C84] flex items-start justify-center px-4 py-12">
      <Toaster position="top-right" richColors />
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#1B2380]/10 flex items-center justify-center">
              <Megaphone size={22} className="text-[#1B2380]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Pannello Admin Assietica</h1>
              <p className="text-sm text-gray-500">{user ? `Accesso: ${user.name} (${user.email})` : 'Area riservata'}</p>
            </div>
          </div>
          {user && (
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors">
              <LogOut size={15} /> Esci
            </button>
          )}
        </div>

        {!user ? (
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="nome@assietica.it" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="admin-password-input" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} pl-9 pr-10`} placeholder="La tua password" required />
                <button type="button" onClick={() => setShowPassword((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Mostra password">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-[#1B2380] hover:bg-[#2A3299] text-white font-semibold py-2.5 rounded-lg transition-colors duration-300 disabled:opacity-60">
              {loading ? 'Verifica...' : 'Accedi'}
            </button>
            <button data-testid="admin-forgot-btn" type="button" onClick={forgot} className="w-full text-sm text-[#1B2380] hover:underline">
              Password dimenticata? Ricevi il link via email
            </button>
          </form>
        ) : (
          <div className="space-y-10">
            {/* Avviso */}
            <form onSubmit={saveAnnouncement} className="space-y-5">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Megaphone size={18} className="text-[#1B2380]" /> Comunicazioni di servizio</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Testo dell'avviso</label>
                <textarea data-testid="announcement-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1000} className={`${inputCls} resize-y`} placeholder="Es. Chiusura straordinaria per formazione..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Data di scadenza <span className="text-gray-400 font-normal">(sparisce automaticamente)</span></label>
                <input data-testid="announcement-expiry-input" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={inputCls} />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="announcement-active-checkbox" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-4 h-4 accent-[#1B2380]" />
                <span className="text-sm text-gray-700">Avviso attivo (visibile sul sito)</span>
              </label>
              <button data-testid="announcement-save-btn" type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-[#1B2380] hover:bg-[#2A3299] text-white font-semibold py-2.5 rounded-lg transition-colors duration-300 disabled:opacity-60">
                <Save size={16} /> {loading ? 'Salvataggio...' : 'Salva avviso'}
              </button>
            </form>

            {/* Statistiche */}
            <form onSubmit={saveImpact} className="pt-8 border-t border-gray-200 space-y-5">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><BarChart3 size={18} className="text-[#0E8C84]" /> Statistiche Impatto</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Titolo</label>
                <input data-testid="impact-title-input" type="text" value={impact.title} onChange={(e) => setImpact((p) => ({ ...p, title: e.target.value }))} maxLength={200} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sottotitolo</label>
                <textarea data-testid="impact-subtitle-input" value={impact.subtitle} onChange={(e) => setImpact((p) => ({ ...p, subtitle: e.target.value }))} rows={2} maxLength={500} className={`${inputCls} resize-y`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nota data</label>
                <input data-testid="impact-date-input" type="text" value={impact.date_note} onChange={(e) => setImpact((p) => ({ ...p, date_note: e.target.value }))} maxLength={100} className={inputCls} />
              </div>
              <div className="space-y-4">
                {impact.counters.map((c) => (
                  <div key={c.id} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{COUNTER_NAMES[c.id]}</p>
                    <div className="flex gap-3">
                      <div className="w-28 flex-shrink-0">
                        <label className="block text-xs text-gray-500 mb-1">Numero</label>
                        <input data-testid={`impact-value-${c.id}`} type="number" min="0" value={c.value} onChange={(e) => updateCounter(c.id, 'value', e.target.value)} className={inputCls} />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Descrizione</label>
                        <input data-testid={`impact-label-${c.id}`} type="text" value={c.label} maxLength={300} onChange={(e) => updateCounter(c.id, 'label', e.target.value)} className={inputCls} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button data-testid="impact-save-btn" type="submit" disabled={savingImpact} className="w-full flex items-center justify-center gap-2 bg-[#0E8C84] hover:bg-[#0B7770] text-white font-semibold py-2.5 rounded-lg transition-colors duration-300 disabled:opacity-60">
                <Save size={16} /> {savingImpact ? 'Salvataggio...' : 'Salva statistiche'}
              </button>
            </form>

            {/* Contenuti sito (CMS) */}
            <AdminContenuti />

            {/* Costruttore moduli */}
            <AdminModuli />

            {/* Richieste dai moduli */}
            <AdminRichiesteModuli />

            {/* Documenti PDF */}
            <div className="pt-8 border-t border-gray-200 space-y-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><FileText size={18} className="text-[#E67E22]" /> Documenti precontrattuali (PDF)</h2>
              <p className="text-sm text-gray-500">I PDF caricati qui vengono pubblicati nella pagina Precontrattuale, sezione "Documentazione scaricabile". Solo formato PDF, max 10 MB.</p>
              {docs.map((d) => (
                <div key={d.id} className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{d.nome}</p>
                    {d.uploaded ? (
                      <p className="text-xs text-[#0E8C84] flex items-center gap-1 mt-1">
                        <CheckCircle2 size={13} /> Pubblicato: {d.filename} ({(d.updated_at || '').slice(0, 10)})
                      </p>
                    ) : (
                      <p className="text-xs text-amber-600 mt-1">Non ancora caricato</p>
                    )}
                  </div>
                  <label className={`inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg cursor-pointer transition-colors duration-300 ${uploadingDoc === d.id ? 'bg-gray-300 text-gray-500' : 'bg-[#E67E22] hover:bg-[#d8701b] text-white'}`}>
                    <Upload size={14} /> {uploadingDoc === d.id ? 'Caricamento...' : d.uploaded ? 'Sostituisci' : 'Carica PDF'}
                    <input data-testid={`doc-upload-${d.id}`} type="file" accept="application/pdf" className="hidden" disabled={uploadingDoc === d.id} onChange={(e) => uploadDoc(d.id, e.target.files[0])} />
                  </label>
                </div>
              ))}
            </div>

            {/* Risk Experience */}
            <div className="pt-8 border-t border-gray-200 space-y-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><ShieldCheck size={18} className="text-[#0E8C84]" /> Risk Experience — Richieste</h2>

              {/* Generatore link partner */}
              <div data-testid="risk-partner-link-box" className="bg-[#1B2380]/5 border border-[#1B2380]/15 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-gray-700">Link personalizzato per i Partner</p>
                <p className="text-xs text-gray-500">Inserisci il codice del partner: le richieste arrivate dal suo link gli verranno attribuite.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    data-testid="risk-partner-code-input"
                    type="text"
                    placeholder="Codice partner (es. COOPAS01)"
                    value={partnerCode}
                    onChange={(e) => setPartnerCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                    className={`${inputCls} sm:max-w-[220px]`}
                  />
                  <div className="flex-1 flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 overflow-x-auto whitespace-nowrap">
                    {partnerCode ? `https://assietica.it/risk-experience?p=${partnerCode}` : 'https://assietica.it/risk-experience?p=...'}
                  </div>
                  <button
                    data-testid="risk-partner-copy-btn"
                    disabled={!partnerCode}
                    onClick={() => {
                      navigator.clipboard.writeText(`https://assietica.it/risk-experience?p=${partnerCode}`);
                      toast.success(`Link per ${partnerCode} copiato negli appunti`);
                    }}
                    className="inline-flex items-center justify-center gap-1.5 bg-[#1B2380] hover:bg-[#2A3299] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-40"
                  >
                    Copia link
                  </button>
                </div>
              </div>

              {riskReqs.length === 0 ? (
                <p data-testid="risk-admin-empty" className="text-sm text-gray-400">Nessuna richiesta ricevuta finora.</p>
              ) : (
                riskReqs.map((r) => {
                  const pending = !['validata', 'gestita'].includes(r.status);
                  const isReport = r.tipo === 'report_email';
                  return (
                    <div key={r.id} data-testid={`risk-admin-req-${r.id}`} className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${isReport ? 'bg-[#1B2380]/10 text-[#1B2380]' : 'bg-[#0E8C84]/15 text-[#0B7770]'}`}>
                          {isReport ? 'REPORT VIA EMAIL' : 'CONSULENZA'}
                        </span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${pending ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'}`}>
                          {r.status === 'in_attesa_validazione' ? 'In attesa di validazione' : r.status === 'nuova' ? 'Nuova' : r.status === 'validata' ? 'Validata e inviata' : 'Gestita'}
                        </span>
                        {r.partner_code && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#8FE3DA]/30 text-[#8a6d00]">Partner: {r.partner_code}</span>}
                      </div>
                      <p className="text-sm font-semibold text-gray-800">{r.nome_ente} — {r.referente}</p>
                      <p className="text-xs text-gray-500">{r.cf_piva ? `CF/P.IVA: ${r.cf_piva} · ` : ''}{r.email} · {r.telefono || 'cell. n.d.'} · {(r.created_at || '').slice(0, 10)} · Aree da approfondire: {r.aree_ambra}/6</p>
                      {r.messaggio && <p className="text-xs text-gray-600 italic">"{r.messaggio}"</p>}
                      <div className="flex flex-wrap items-center gap-4">
                        <button
                          data-testid={`risk-admin-pdf-${r.id}`}
                          onClick={() => previewRiskPdf(r.id)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B2380] hover:underline"
                        >
                          <FileSearch size={13} /> Anteprima report PDF
                        </button>
                        {r.cf_piva && (
                          <button
                            data-testid={`risk-admin-vies-${r.id}`}
                            onClick={() => verificaCfPiva(r.cf_piva)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B2380] hover:underline"
                          >
                            <ExternalLink size={13} /> {/^\d{11}$/.test(r.cf_piva) ? 'Verifica P.IVA su VIES' : 'Verifica CF su Agenzia Entrate'}
                          </button>
                        )}
                      </div>
                      {pending && (
                        <div className="flex flex-col sm:flex-row gap-2 pt-1">
                          {isReport && (
                            <input
                              data-testid={`risk-admin-nota-${r.id}`}
                              type="text"
                              placeholder="Nota del consulente (facoltativa, inserita nell'email)"
                              value={riskNote[r.id] || ''}
                              onChange={(e) => setRiskNote((p) => ({ ...p, [r.id]: e.target.value }))}
                              className={`${inputCls} flex-1`}
                            />
                          )}
                          <button
                            data-testid={`risk-admin-valida-${r.id}`}
                            onClick={() => validaRisk(r.id)}
                            disabled={validating === r.id}
                            className="inline-flex items-center justify-center gap-2 bg-[#0E8C84] hover:bg-[#329a95] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
                          >
                            <Send size={14} /> {validating === r.id ? 'Invio...' : isReport ? 'Valida e invia il report' : 'Segna come gestita'}
                          </button>
                        </div>
                      )}
                      {r.status === 'validata' && (
                        <p className="text-xs text-[#0E8C84]">Inviato da {r.validated_by} il {(r.validated_at || '').slice(0, 10)}{r.nota_validazione ? ` — nota: "${r.nota_validazione}"` : ''}</p>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Cambio password */}
            <form onSubmit={changePwd} className="pt-8 border-t border-gray-200 space-y-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><KeyRound size={18} className="text-[#1B2380]" /> Cambia la tua password</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="pwd-current-input" type="password" value={pwdForm.current} onChange={(e) => setPwdForm((p) => ({ ...p, current: e.target.value }))} className={inputCls} placeholder="Password attuale" required />
                <input data-testid="pwd-new-input" type="password" value={pwdForm.nuova} onChange={(e) => setPwdForm((p) => ({ ...p, nuova: e.target.value }))} className={inputCls} placeholder="Nuova password (min 8 caratteri)" required />
              </div>
              <button data-testid="pwd-change-btn" type="submit" disabled={changingPwd} className="w-full border-2 border-[#1B2380] text-[#1B2380] hover:bg-[#1B2380]/5 font-semibold py-2.5 rounded-lg transition-colors duration-300 disabled:opacity-60">
                {changingPwd ? 'Aggiornamento...' : 'Aggiorna password'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
